import { debounce } from 'lodash';
import pluralize from 'pluralize';
import moment from 'moment';

export const capitalize = str => {
  if (str) {
    str = str.replace(/\b\w/g, l => l.toUpperCase());
  }

  return str;
};

export const possessive = str => {
  if (str) {
    if (str[str.length - 1] === 's') {
      return `${str}'`;
    }

    return `${str}'s`;
  }

  return str;
};

export const title = str => {
  if (str) {
    return capitalize(str.replace('_', ' '));
  }
};

export const deslugify = str => {
  if (str) {
    str = str.replace(/[-_]/g, ' ');
    str = str.replace(/\b\w/g, l => l.toUpperCase());
  }

  return str;
};

export const slugify = str => {
  if (str) {
    // remove spaces
    str = str.replace(/^\s+|\s+$/g, '');
    str = str.toLowerCase();

    // remove accents, swap ñ for n, etc
    var from = 'ãàáäâẽèéëêìíïîõòóöôùúüûñç·/_,:;';
    var to = 'aaaaaeeeeeiiiiooooouuuunc------';

    for (var i = 0, l = from.length; i < l; i++) {
      str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }

    str = str
      .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
      .replace(/\s+/g, '-') // collapse whitespace and replace by -
      .replace(/-+/g, '-'); // collapse dashes
  }

  return str;
};

export const isEditor = user => {
  if (user && user.role_id <= 2) {
    return true;
  }

  return false;
};

export const isAdmin = user => {
  if (user && user.role_id <= 1) {
    return true;
  }

  return false;
};

export const canUpdate = user => {
  if (isEditor(user)) {
    return true;
  }

  return false;
};

export const canRemove = user => {
  if (isAdmin(user)) {
    return true;
  }

  return false;
};

export const formatUser = str => {
  return possessive(capitalize(str));
};

export const showPrompt = (message = 'Do you really want to delete?') => {
  if (!window.confirm(message)) {
    return false;
  }
};

export const covertToPennies = (amount) => {
  if (typeof amount === 'string') {
    // console.log('amount', amount);
    const parsed = amount.replace(/^\$/, '');
    // console.log('remove $', parseFloat(parsed) * 100);
    return parseFloat(parsed) * 100;
  }

  return amount;
}

export const currency = (amount, symbol = '$', locale = 'USD') => {
  if (
    typeof amount !== 'number' &&
    typeof amount !== 'undefined' &&
    amount !== null
  ) {
    return;
  }

  if (amount < 0) {
    return `-${symbol}${Math.abs(amount * 0.01).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  if (amount) {
    return `${symbol}${(amount * 0.01).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  return '$0.00';
};

export const infiniteScroll = fn => {
  debounce(
    (window.onscroll = () => {
      let bottomOfWindow =
        document.documentElement.scrollTop + window.innerHeight ===
        document.documentElement.offsetHeight;

      if (bottomOfWindow) {
        fn();
      }
    }),
    300
  );
};

export const remove = (items, id) => {
  return items.filter(o => (o === o.id) !== id);
};

export const lastLetter = str => {
  return str[str.length - 1];
};

const removeNullKeys = obj => {
  // remove null obj keys
  const newObj = Object.assign({}, obj);

  Object.keys(newObj).forEach(o => {
    const val = newObj[o];

    if (Array.isArray(val)) {
      if (val.length === 0 || val[0] === '[]') {
        delete newObj[o];
      }
    } else if (val === null || val === 'null') {
      delete newObj[o];
    }
  });

  return newObj;
};

export const buildUrl = (url, filters) => {
  // add ? for params
  let str = url.search('=') === -1 ? `${url}?` : url;

  const newObj = removeNullKeys(filters);
  const keys = Object.keys(newObj);

  // check for array values
  keys.forEach(key => {
    if (Array.isArray(newObj[key])) {
      newObj[key] = `[${newObj[key].join(',')}]`;
    }
  });

  keys.forEach(key => {
    if (lastLetter(str) === '?') {
      str = `${str}${key}=${newObj[key]}`;
    } else {
      str = `${str}&${key}=${newObj[key]}`;
    }
  });

  return str;
};

export const plural = (str, num) => {
  return str ? pluralize(str, num) : '';
};

export const sort = (items, direction, field) => {
  const newItems = [...items];

  if (typeof newItems[0][field] === 'string') {
    if (direction === 'asc') {
      newItems.sort((a, b) => {
        const nameA = a[field].toUpperCase();
        const nameB = b[field].toUpperCase();

        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        }

        return 0;
      });
    } else {
      newItems.sort((a, b) => {
        const nameA = a[field].toUpperCase();
        const nameB = b[field].toUpperCase();

        if (nameA > nameB) {
          return -1;
        } else if (nameA < nameB) {
          return 1;
        }

        return 0;
      });
    }
  } else {
    if (direction === 'asc') {
      newItems.sort((a, b) => a[field] - b[field]);
    } else {
      newItems.sort((a, b) => b[field] - a[field]);
    }
  }

  return newItems;
};

export const humanDate = date => {
  return moment(date).fromNow();
};

export const decamelize = (str, separator = ' ') => {
  return str
    .replace(/([a-z\d])([A-Z])/g, '$1' + separator + '$2')
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, '$1' + separator + '$2')
    .toLowerCase();
};

export const deSnake = (str, separator = ' ') => {
  return str.replace(/_/g, separator).toLowerCase();
}

export const getCookie = (name) => {
  const cookies = document.cookie;

  if (cookies) {
    return cookies
    .split('; ')
    .find(row => row.startsWith(name))
    .split('=')[1];
  }
}

export const getImagePath = (image) => {
  return (image) ? `/pony-images/${image?.image_path}` : null;
}

export default {
  slugify,
  possessive,
  capitalize,
  deslugify,
  formatUser,
  canRemove,
  canUpdate,
  isEditor,
  isAdmin,
  currency,
  infiniteScroll,
  remove,
  showPrompt,
  humanDate,
  getCookie,
  covertToPennies,
  deSnake,
  getImagePath,
};
